/*
 * _customled-page-accordion.js  (Maranello M1.10.x — TEMPORARY)
 *
 * Vanilla-JS port of the Activ8 LIVE jQuery accordion handler from
 * assets/site.js. Activates the `.gt-accordion > (h4 + div)` pattern
 * that the merchant's page content (e.g., /pages/custom-led-policies)
 * uses as-is from the legacy Activ8 setup.
 *
 * This is a STOPGAP: it keeps the existing page body content working
 * without requiring re-authoring during the parallel-live period. Once
 * the live Activ8 theme is retired, the policies / similar pages can
 * be migrated to Maranello's native `sections/expandable.liquid`
 * (block-based, theme-editor-tunable) and this script can be deleted
 * with no other cleanup needed.
 *
 * Self-contained: no jQuery, no dependencies. No-ops on pages without
 * a `.gt-accordion` element, so safe to load globally.
 *
 * Behavior:
 *   - On DOMContentLoaded, find every `.gt-accordion` container.
 *   - For each direct-child `<h4>`, treat the immediately following
 *     sibling element (typically a `<div>` of paragraphs) as the
 *     collapsible body.
 *   - Initial state: all sections collapsed.
 *   - Click the `<h4>` to toggle. Sets/removes the `.active` class on
 *     the h4 and toggles a `[hidden]` boolean attribute on the body
 *     for screen-reader semantics (plus inline visibility via CSS).
 *   - Sets ARIA attributes: h4 gets role="button", aria-expanded,
 *     tabindex=0; body gets role="region" + aria-labelledby.
 *   - Hash deep-link: if the URL ends with `#some-h4-id` and that ID
 *     matches an h4 inside a `.gt-accordion`, auto-expand that section
 *     on load and scroll to it (matches typical FAQ page UX).
 *
 * Owned, prefixed with `_customled-`. Loaded from
 * snippets/_customled-stylesheets.liquid.
 */
(function () {
  "use strict";

  var ACCORDION_SELECTOR = ".gt-accordion";

  function setExpanded(h4, body, expanded) {
    if (expanded) {
      h4.classList.add("active");
      h4.setAttribute("aria-expanded", "true");
      body.removeAttribute("hidden");
    } else {
      h4.classList.remove("active");
      h4.setAttribute("aria-expanded", "false");
      body.setAttribute("hidden", "");
    }
  }

  function initAccordion(accordion) {
    if (accordion.__customledAccordionInit) return;
    accordion.__customledAccordionInit = true;

    // Pair each h4 with its next sibling element (the body).
    var pairs = [];
    Array.prototype.forEach.call(accordion.children, function (child) {
      if (child.tagName === "H4") {
        var body = child.nextElementSibling;
        if (body) pairs.push({ h4: child, body: body });
      }
    });

    pairs.forEach(function (pair, idx) {
      var h4 = pair.h4;
      var body = pair.body;

      // Ensure h4 has a STABLE, slug-based ID we can reference from
      // aria-labelledby and from external #fragment links (footer policy
      // items, product-body warranty links). Preserve any explicit ID set
      // in the page body (e.g. #warranty, #satisfaction-guarantee). Derive
      // the slug from the heading text ("Return Policy" -> "return-policy")
      // and dedupe with a numeric suffix if it collides with an existing ID.
      if (!h4.id) {
        var base = (h4.textContent || "")
          .toLowerCase()
          .replace(/&[a-z]+;/g, " ")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        if (!base) base = "section-" + idx;
        var id = base;
        var n = 2;
        while (document.getElementById(id)) {
          id = base + "-" + n;
          n += 1;
        }
        h4.id = id;
      }
      if (!body.id) {
        body.id = h4.id + "-body";
      }

      // ARIA + keyboard semantics. We don't reuse <details>/<summary>
      // because we want to preserve the merchant's exact h4 + div HTML
      // structure (the live site uses the same elements). Instead we
      // augment the existing elements with role + aria + tabindex.
      h4.setAttribute("role", "button");
      h4.setAttribute("tabindex", "0");
      h4.setAttribute("aria-controls", body.id);
      body.setAttribute("role", "region");
      body.setAttribute("aria-labelledby", h4.id);

      // All collapsed initially.
      setExpanded(h4, body, false);

      h4.addEventListener("click", function () {
        var expanded = h4.classList.contains("active");
        setExpanded(h4, body, !expanded);
      });

      h4.addEventListener("keydown", function (event) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          var expanded = h4.classList.contains("active");
          setExpanded(h4, body, !expanded);
        }
      });
    });

    return pairs;
  }

  // Height of the sticky/fixed site header pinned to the top of the viewport.
  // Deep-link scrolling must offset for it, otherwise the heading parks at y=0
  // hidden BEHIND the header (the theme header group is position:sticky, ~174px).
  function stickyHeaderOffset() {
    var max = 0;
    var sel =
      ".shopify-section-group-header-group, [class*='shopify-section-group-header']," +
      " header, .header, sticky-header";
    var els = document.querySelectorAll(sel);
    Array.prototype.forEach.call(els, function (el) {
      var cs = getComputedStyle(el);
      if (cs.position !== "fixed" && cs.position !== "sticky") return;
      // Where this element's bottom edge lands ONCE PINNED to the top: its CSS
      // pin offset (`top`) plus its own height. We compute it this way instead
      // of reading getBoundingClientRect().top because at scrollY=0 the sticky
      // header still sits at its in-flow position (e.g. pushed down to top:40
      // by a static announcement bar above it) — but it pins to top:0 the moment
      // you scroll, and a deep-link scroll always scrolls the page. The old
      // `br.top <= 1` test therefore measured 0 and set no margin. Only count
      // top-anchored headers (small pin offset); a sticky element further down
      // the page is not something the deep-link scroll needs to clear.
      var pin = parseFloat(cs.top);
      if (isNaN(pin) || pin > 80) return;
      var pinnedBottom = pin + el.getBoundingClientRect().height;
      if (pinnedBottom > max) max = pinnedBottom;
    });
    return Math.round(max);
  }

  // Apply that offset as scroll-margin-top on every accordion heading, so a
  // scrollIntoView({block:"start"}) (and native #hash scrolling) lands the
  // HEADING just below the sticky header instead of underneath it. +16px breathing room.
  function applyScrollMargin() {
    var offset = stickyHeaderOffset();
    if (!offset) return;
    document.querySelectorAll(ACCORDION_SELECTOR + " > h4").forEach(function (h4) {
      h4.style.scrollMarginTop = offset + 16 + "px";
    });
  }

  // Destination scrollY that parks the HEADING just below the sticky header.
  // Re-derived from a freshly-measured offset on every call so it is correct
  // even if the offset was 0 earlier (header not yet position:sticky).
  function targetScrollY(target) {
    var y =
      target.getBoundingClientRect().top +
      window.pageYOffset -
      stickyHeaderOffset() -
      16;
    return y < 0 ? 0 : y;
  }

  function scrollHeadingIntoView(target, smooth) {
    applyScrollMargin(); // keep native #hash / :target scrolling correct too
    var y = targetScrollY(target);
    // Corrective snaps pass smooth=false and MUST be instant: the page sets
    // `scroll-behavior: smooth` in CSS, so behavior:"auto" would animate (and
    // animated scrolls silently no-op in a backgrounded tab, where rAF is
    // paused). "instant" forces an immediate jump regardless. The 2-arg
    // fallback is also instant for any engine that rejects the "instant" token.
    try {
      window.scrollTo({ top: y, behavior: smooth ? "smooth" : "instant" });
    } catch (e) {
      window.scrollTo(0, y);
    }
  }

  // Hash deep-link handler: if URL ends with #<some-h4-id> and that h4
  // is inside a .gt-accordion, expand its section and scroll to it.
  // Runs on initial load + on subsequent hashchange events (e.g. user
  // clicks a TOC link on the same page).
  function honorHashDeepLink() {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    var id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (e) {
      id = hash.slice(1);
    }
    if (!id) return;
    var target = document.getElementById(id);
    if (!target) return;
    // Only act on accordion h4s — leave other anchor links alone.
    if (target.tagName !== "H4") return;
    var accordion = target.closest(ACCORDION_SELECTOR);
    if (!accordion) return;
    var body = target.nextElementSibling;
    if (!body) return;

    setExpanded(target, body, true);

    // On initial page load two things can knock the heading out of place after
    // our first scroll: (1) the theme upgrades its header to position:sticky a
    // beat after DOMContentLoaded — the offset jumps from 0 to its real height
    // and the heading ends up hidden behind the header; (2) the browser's
    // automatic scroll restoration yanks the page back to y=0 after load.
    // Defeat both: turn off restoration, animate to the destination, then snap
    // to a freshly-measured destination a few times if something moved us by a
    // large amount (a header-init shift or a restore-to-0 — never a small user
    // nudge, which we leave alone).
    if ("scrollRestoration" in history) {
      try {
        history.scrollRestoration = "manual";
      } catch (e) {}
    }
    requestAnimationFrame(function () {
      scrollHeadingIntoView(target, true);
    });
    // Watchdog: only correct once the page has SETTLED at the wrong place, so we
    // never interrupt the smooth glide above mid-animation. "Wrong place" means
    // scroll restoration yanked us back to ~0, or the first scroll landed with a
    // stale offset. Corrections are instant (see scrollHeadingIntoView). In a
    // backgrounded tab the smooth glide never runs, so the page reads as settled
    // at 0 immediately and the first instant correction lands it.
    var checks = 0;
    var lastY = null;
    var iv = setInterval(function () {
      checks += 1;
      var y = window.pageYOffset;
      var settled = lastY !== null && Math.abs(y - lastY) < 2;
      if (settled && Math.abs(y - targetScrollY(target)) > 30) {
        scrollHeadingIntoView(target, false);
      }
      lastY = window.pageYOffset;
      if (checks >= 8) clearInterval(iv);
    }, 150);
  }

  function init() {
    var accordions = document.querySelectorAll(ACCORDION_SELECTOR);
    if (!accordions.length) return;
    Array.prototype.forEach.call(accordions, initAccordion);
    applyScrollMargin();
    honorHashDeepLink();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.addEventListener("hashchange", honorHashDeepLink);
})();
